// Accessible overlays built on Radix primitives (focus trap, escape, aria).
import type { ReactNode } from "react";
import {
  Dialog as D,
  DropdownMenu as DM,
  Popover as P,
  Switch as S,
  Tabs as T,
  Tooltip as TT,
} from "radix-ui";
import { X } from "lucide-react";
import { cn } from "../cn";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  closeLabel?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  closeLabel = "Close",
}: DialogProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 animate-fade-in bg-slate-950/40 backdrop-blur-[2px]" />
        <D.Content
          className={cn(
            "fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 animate-dialog-in flex-col rounded-[var(--radius-card)] border border-line bg-paper shadow-[var(--shadow-pop)] focus:outline-none",
            size === "sm" ? "max-w-sm" : size === "lg" ? "max-w-2xl" : "max-w-lg",
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-5">
            <div>
              <D.Title className="text-lg font-semibold text-ink">{title}</D.Title>
              {description ? (
                <D.Description className="mt-1 text-sm text-ink-2">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{title}</D.Description>
              )}
            </div>
            <D.Close
              className="-mr-2 inline-flex size-8 items-center justify-center rounded-[var(--radius-control)] text-ink-3 transition-colors hover:bg-hover hover:text-ink"
              aria-label={closeLabel}
            >
              <X className="size-4.5" aria-hidden />
            </D.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
          {footer && (
            <div className="flex flex-wrap justify-end gap-2 rounded-b-[var(--radius-card)] border-t border-line bg-subtle px-6 py-3">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/** Left side drawer (used for the navigator on small screens). */
export function Drawer({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 animate-fade-in bg-slate-950/40 backdrop-blur-[2px]" />
        <D.Content className="fixed inset-y-0 left-0 z-50 w-[var(--spacing-sidebar)] max-w-[85vw] animate-drawer-in overflow-y-auto bg-nav shadow-[var(--shadow-pop)] focus:outline-none">
          <D.Title className="sr-only">{title}</D.Title>
          <D.Description className="sr-only">{title}</D.Description>
          {children}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
  id,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <S.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full bg-slate-900/20 shadow-[inset_0_1px_2px_rgb(0_0_0/0.08)] transition-colors data-[state=checked]:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
    >
      <S.Thumb className="block size-5 translate-x-0.5 rounded-full bg-white shadow-[0_1px_3px_rgb(0_0_0/0.2)] transition-transform duration-200 data-[state=checked]:translate-x-[22px]" />
    </S.Root>
  );
}

export function Tooltip({
  content,
  children,
  side = "top",
}: {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
}) {
  return (
    <TT.Provider delayDuration={300}>
      <TT.Root>
        <TT.Trigger asChild>{children}</TT.Trigger>
        <TT.Portal>
          <TT.Content
            side={side}
            sideOffset={6}
            className="z-50 animate-fade-in rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-[var(--shadow-pop)]"
          >
            {content}
          </TT.Content>
        </TT.Portal>
      </TT.Root>
    </TT.Provider>
  );
}

export const Menu = {
  Root: DM.Root,
  Trigger: DM.Trigger,
  Content({ children, align = "end" }: { children: ReactNode; align?: "start" | "end" }) {
    return (
      <DM.Portal>
        <DM.Content
          align={align}
          sideOffset={6}
          className="z-50 min-w-52 animate-pop-in rounded-[var(--radius-card)] border border-line bg-paper p-1 text-sm text-ink shadow-[var(--shadow-pop)]"
        >
          {children}
        </DM.Content>
      </DM.Portal>
    );
  },
  Item({
    children,
    onSelect,
    destructive,
  }: {
    children: ReactNode;
    onSelect?: () => void;
    destructive?: boolean;
  }) {
    return (
      <DM.Item
        onSelect={onSelect}
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 outline-none data-[highlighted]:bg-hover",
          destructive && "text-danger data-[highlighted]:bg-danger-bg",
        )}
      >
        {children}
      </DM.Item>
    );
  },
  Label({ children }: { children: ReactNode }) {
    return <DM.Label className="px-3 pt-2 pb-1 text-xs font-medium text-ink-2">{children}</DM.Label>;
  },
  Separator() {
    return <DM.Separator className="-mx-1 my-1 h-px bg-line" />;
  },
};

/** Non-modal floating panel (filters, column chooser). */
export const Popover = {
  Root: P.Root,
  Trigger: P.Trigger,
  Anchor: P.Anchor,
  Close: P.Close,
  Content({
    children,
    align = "start",
    className,
    label,
  }: {
    children: ReactNode;
    align?: "start" | "center" | "end";
    className?: string;
    label: string;
  }) {
    return (
      <P.Portal>
        <P.Content
          align={align}
          sideOffset={6}
          collisionPadding={12}
          aria-label={label}
          className={cn(
            "z-50 max-h-[min(70vh,var(--radix-popover-content-available-height))] animate-pop-in overflow-y-auto rounded-[var(--radius-card)] border border-line bg-paper p-3 text-sm text-ink shadow-[var(--shadow-pop)] focus:outline-none",
            className,
          )}
        >
          {children}
        </P.Content>
      </P.Portal>
    );
  },
};

export const Tabs = {
  Root: T.Root,
  List({ children, className, label }: { children: ReactNode; className?: string; label: string }) {
    return (
      <T.List aria-label={label} className={cn("flex gap-1 overflow-x-auto border-b border-line", className)}>
        {children}
      </T.List>
    );
  },
  Trigger({ value, children }: { value: string; children: ReactNode }) {
    return (
      <T.Trigger
        value={value}
        className="relative -mb-px h-11 shrink-0 px-4 text-sm font-medium text-ink-2 transition-colors hover:text-ink data-[state=active]:text-primary-dark data-[state=active]:after:absolute data-[state=active]:after:inset-x-2 data-[state=active]:after:bottom-0 data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-full data-[state=active]:after:bg-primary-dark"
      >
        {children}
      </T.Trigger>
    );
  },
  Content: T.Content,
};
